import { ApolloClient, DataProxy, ExecutionPatchResult, NormalizedCacheObject, useMutation, useQuery } from '@apollo/client';
import {
  NotificationsData,
  NotificationsVars,
  SendNotificationData,
  SendNotificationVar,
  SendUserNotificationData,
  SendUserNotificationVars,
  SentNotificationsData,
  SentNotificationsVars,
  UpdateAllNotificationsAction,
  UpdateAllNotificationsVars,
  UpdateNotificationStateData,
  UpdateNotificationStateVars
} from '../gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import GraphQL from '../gql';
import { NOTIFICATION_POLL_INTERVAL } from '../../shared/constants';
import { NoticeSender } from 'notifications/models/NoticeSender';
import { isNil } from 'lodash';

let NotificationApolloClient: ApolloClient<NormalizedCacheObject> = null;

function useNotifications(userkey: number, loadData: boolean = true) {
  if (!NotificationApolloClient) {
    NotificationApolloClient = ApolloClientService.create(
      Config.NOTIFICATION_GQL
    );
  }

  const {
    loading,
    error: loadError,
    data: loaded
  } = useQuery<NotificationsData, NotificationsVars>(
    GraphQL.Query.NOTIFICATIONS,
    {
      client: NotificationApolloClient,
      variables: {
        receiverkey: userkey
      },
      skip: loadData === false || isNil(userkey),
      fetchPolicy: 'cache-and-network',
      pollInterval: NOTIFICATION_POLL_INTERVAL
    }
  );

  const {
    loading: sentLoading,
    error: loadSentError,
    data: sentNotifications
  } = useQuery<SentNotificationsData, SentNotificationsVars>(
    GraphQL.Query.SENT_NOTIFICATIONS,
    {
      client: NotificationApolloClient,
      variables: {
        senderkey: userkey
      },
      skip: loadData === false || isNil(userkey),
      fetchPolicy: 'cache-and-network',
      pollInterval: NOTIFICATION_POLL_INTERVAL
    }
  );

  const [
    _updateNotificationState,
    { loading: updating, error: updateError, data: updated }
  ] = useMutation<UpdateNotificationStateData, UpdateNotificationStateVars>(
    GraphQL.Mutation.UPDATE_NOTIFICATION_STATE,
    {
      client: NotificationApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionPatchResult<UpdateNotificationStateData>
      ) => {
        const notificationsData = cache.readQuery<
          NotificationsData,
          NotificationsVars
        >({
          query: GraphQL.Query.NOTIFICATIONS,
          variables: {
            receiverkey: userkey
          }
        });
        const copy = [...notificationsData.notifications];
        const updated = data.updateNotificationState;
        const updatedIndex = copy.findIndex(
          (n) => n.noticekey === updated.noticekey
        );
        if (updatedIndex < 0) {
          return;
        }
        if (updated.hidden) {
          copy.splice(updatedIndex, 1);
        } else {
          copy.splice(updatedIndex, 1, { ...updated });
        }
        cache.writeQuery<NotificationsData>({
          query: GraphQL.Query.NOTIFICATIONS,
          data: { notifications: copy },
          variables: { receiverkey: userkey }
        });
      }
    }
  );

  const [
    _sendGroupNotification,
    { loading: sending, error: sendingError, data: sendData }
  ] = useMutation<SendNotificationData, SendNotificationVar>(
    GraphQL.Mutation.SEND_GROUP_NOTIFICATION,
    {
      client: NotificationApolloClient
    }
  );

  function updateNotificationState(
    noticekey: number,
    receiverkey: number,
    markAsRead: boolean,
    hidden: boolean
  ) {
    _updateNotificationState({
      variables: {
        noticekey,
        receiverkey,
        markAsRead,
        hidden
      }
    });
  }

  async function sendGroupNotification(
    senderkey: number,
    groupkey: number,
    noticeAttributes: NoticeSender
  ) {
    await _sendGroupNotification({
      variables: {
        senderkey,
        groupkey,
        noticeAttributes
      }
    });
  }

  const [
    _updateAllNotifications,
    { loading: updatingAll, error: updateAllError, data: updateAll }
  ] = useMutation<UpdateAllNotificationsVars>(
    GraphQL.Mutation.UPDATE_ALL_NOTIFICATIONS,
    {
      client: NotificationApolloClient,
      refetchQueries: [
        {
          query: GraphQL.Query.NOTIFICATIONS,
          variables: {
            receiverkey: userkey
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

  const updateAllNotifications = (
    receiverkey: number,
    action: UpdateAllNotificationsAction
  ) => {
    _updateAllNotifications({
      variables: {
        receiverkey,
        action
      }
    });
  };

  const [
    _sendUserNotification,
    { loading: sendingMessage, error: sendMessageError, data: sendMessageData }
  ] = useMutation<SendUserNotificationData, SendUserNotificationVars>(
    GraphQL.Mutation.SEND_USER_NOTIFICATION,
    {
      client: NotificationApolloClient
    }
  );

  const sendUserNotification = async (
    senderkey: number,
    receiverkeys: number[],
    noticeAttributes: NoticeSender
  ) => {
    await _sendUserNotification({
      variables: {
        senderkey,
        receiverkeys,
        noticeAttributes
      }
    });
  };

  return {
    loading,
    loadError,
    receivedNotifications: loaded?.notifications,
    sentNotifications: sentNotifications?.sentNotifications,
    updating,
    updateError,
    updated,
    updateNotificationState,
    sendGroupNotification,
    sending,
    sendingError,
    sendData,
    updateAllNotifications,
    sendUserNotification,
    sendingMessage,
    sendMessageData
  };
}

export default useNotifications;
